import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const POLL_MS = 60000;

/**
 * Approval phase 3: header link to Onaylarım with the number of approvals
 * waiting for the signed-in user (polled like the notification bell: every
 * minute and when the window gets focus; `approvals:changed` after a
 * decision on this page refreshes it at once).
 */
export default function ApprovalsBadge() {
  const { t } = useTranslation();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/approvals/inbox/count`);
      setCount(res.data?.count || 0);
    } catch (e) {
      // transient: next tick
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    window.addEventListener('focus', refresh);
    window.addEventListener('approvals:changed', refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('approvals:changed', refresh);
    };
  }, [refresh]);

  const label = count > 0 ? t('approvals.badge.pendingLabel', { n: String(count) }) : t('approvals.menu');
  return (
    <Link
      to="/approvals"
      aria-label={label}
      title={label}
      data-testid="approvals-badge"
      className="relative p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
      {count > 0 && (
        <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-[18px] text-center" data-testid="approvals-badge-count">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

export const notifyApprovalsChanged = () => {
  try {
    window.dispatchEvent(new Event('approvals:changed'));
  } catch (e) {
    // old browsers: the next poll catches up
  }
};
